import React, { useMemo, useState } from 'react';
import { BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type {
  ActionRequiredWaitInterval,
  ActionRequiredWaitSlice,
} from '../../utils/actionRequiredWaitBreakdown';
import { formatWaitDuration } from '../../utils/actionRequiredWaitBreakdown';
import { buildWaitChartColorMap } from '../../utils/actionRequiredWaitChartData';
import ActionRequiredWaitTimelineChart from './ActionRequiredWaitTimelineChart';

type ChartView = 'pie' | 'bar';

interface TaskActionRequiredWaitChartProps {
  slices: ActionRequiredWaitSlice[];
  totalMs: number;
  intervals: ActionRequiredWaitInterval[];
}

interface ChartRow {
  key: string;
  name: string;
  value: number;
  percentage: number;
  color: string;
}

const TaskActionRequiredWaitChart: React.FC<TaskActionRequiredWaitChartProps> = ({
  slices,
  totalMs,
  intervals,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [chartView, setChartView] = useState<ChartView>('pie');

  const colorMap = useMemo(
    () => buildWaitChartColorMap(slices.map(s => s.key)),
    [slices],
  );

  const chartData = useMemo<ChartRow[]>(
    () =>
      slices.map((slice) => ({
        key: slice.key,
        name: slice.label,
        value: slice.durationMs,
        percentage: slice.percentage,
        color: colorMap.get(slice.key) || '#3B82F6',
      })),
    [slices, colorMap],
  );

  if (totalMs <= 0 || chartData.length === 0) {
    return (
      <div className="pm-detail-wait-chart pm-detail-wait-chart--empty">
        <p>No wait history yet.</p>
      </div>
    );
  }

  return (
    <div className="pm-detail-wait-chart">
      <div className="pm-detail-wait-chart-header">
        <p className="pm-detail-wait-chart-subtitle">
          Total elapsed since task creation: {formatWaitDuration(totalMs)}
        </p>
        <div className="pm-detail-wait-chart-toggle" role="group" aria-label="Chart view">
          <button
            type="button"
            className={`pm-detail-wait-chart-toggle-btn ${chartView === 'pie' ? 'is-active' : ''}`}
            onClick={() => setChartView('pie')}
            aria-pressed={chartView === 'pie'}
            title="Pie chart breakdown"
          >
            <PieChartIcon size={13} aria-hidden />
            <span>Pie</span>
          </button>
          <button
            type="button"
            className={`pm-detail-wait-chart-toggle-btn ${chartView === 'bar' ? 'is-active' : ''}`}
            onClick={() => setChartView('bar')}
            aria-pressed={chartView === 'bar'}
            title="Wait time over time"
          >
            <BarChart3 size={13} aria-hidden />
            <span>Over time</span>
          </button>
        </div>
      </div>

      {chartView === 'pie' ? (
        <>
          <div className="pm-detail-wait-chart-visual">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={84}
                  paddingAngle={chartData.length > 1 ? 2 : 0}
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={entry.key}
                      fill={entry.color}
                      stroke="var(--pm-surface, #fff)"
                      strokeWidth={activeIndex === index ? 3 : 1}
                      opacity={activeIndex === null || activeIndex === index ? 1 : 0.45}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: '#111827',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value, _name, item) => {
                    const row = item.payload as ChartRow;
                    return [
                      `${formatWaitDuration(typeof value === 'number' ? value : Number(value) || 0)} (${row.percentage.toFixed(1)}%)`,
                      row.name,
                    ];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="pm-detail-wait-chart-legend">
            {chartData.map((entry, index) => (
              <li
                key={entry.key}
                className={`pm-detail-wait-chart-legend-item ${activeIndex === index ? 'is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <span className="pm-detail-wait-chart-swatch" style={{ backgroundColor: entry.color }} />
                <span className="pm-detail-wait-chart-label">{entry.name}</span>
                <span className="pm-detail-wait-chart-value">
                  {formatWaitDuration(entry.value)} · {entry.percentage.toFixed(1)}%
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="pm-detail-wait-chart-visual pm-detail-wait-chart-visual--timeline">
          <ActionRequiredWaitTimelineChart intervals={intervals} />
        </div>
      )}
    </div>
  );
};

export default TaskActionRequiredWaitChart;
